import cloneDeep from 'lodash/cloneDeep';
import { isMobile, Matrix, RenderTexture, Sprite } from 'pixi.js';
import type { LevelData } from '../game/level/LevelData';
import { StrategyPrioritizeV3 } from '../game/solver/strategies/StrategyPrioritizeV3';
import { BoardModel } from '../game/boardMVC/models/board/BoardModel';
import { BoardView } from '../game/boardMVC/views/board/BoardView';
import { TrayModel } from '../game/boardMVC/models/tray/TrayModel';
import { TRAY_SETTINGS } from '../game/scene/GameSceneConstants';
import { extractLevelRefillColors } from '../game/level/utils/extractLevelRefillColors';
import { BoardViewScheduler } from '../game/boardMVC/models/board/ViewScheduler';
import { LevelTasksTracker } from '../game/tasks/LevelTasksTracker';
import { connectTaskTrackerToBoard } from '../game/tasks/connectTaskTrackerToBoard';
import { GameEndFlowController } from '../game/GameEndFlowController';
import { BoardViewFactory } from '../game/boardMVC/views/board/BoardViewFactory';
import MathUtils from '../utils/MathUtils';
import { getPlatformRemoteConfig } from '../app/firebase/getPlatformRemoteConfig';

export class EnemyBoardComponent {
  public readonly strategy = new StrategyPrioritizeV3();

  public readonly boardModel: BoardModel;

  public readonly trayModel: TrayModel;

  public readonly boardView: BoardView;

  private readonly boardViewRt: RenderTexture = new RenderTexture();

  public readonly boardRTSprite: Sprite = new Sprite().props({
    texture: this.boardViewRt,
  });

  public readonly taskTracker: LevelTasksTracker;

  public readonly endFlowController: GameEndFlowController;

  private resolved = false;

  constructor(
    private readonly levelData: LevelData,
    private readonly difficultyLevel: number,
    private readonly firstTime = false,
  ) {
    const scheduler = new BoardViewScheduler();

    this.boardModel = new BoardModel({
      state: levelData.board,
      scheduler,
    });

    this.trayModel = new TrayModel(
      {
        boardModel: this.boardModel,
        ...TRAY_SETTINGS,
        refills: cloneDeep(levelData.refills),
        refillColors: extractLevelRefillColors(levelData),
      },
      levelData.tray,
    );

    this.taskTracker = new LevelTasksTracker(levelData, {});

    this.boardModel.onScoreUpdated(this.taskTracker.getHexTally());
    this.trayModel.updateProgress(this.taskTracker.getCombinedProgress());
    this.trayModel.refillItems(false);

    connectTaskTrackerToBoard({
      boardModel: this.boardModel,
      boardView: null,
      levelTaskTracker: this.taskTracker,
      trayModel: this.trayModel,
    });

    this.boardView = new BoardView(this.boardModel, new BoardViewFactory()).props({
      visible: false,
    });

    this.boardViewRt.resize(this.boardView.width + 400, this.boardView.height + 400);

    this.boardView.onAnimationFinished.connect((id) => {
      scheduler.finish(id);
    });

    this.endFlowController = new GameEndFlowController({
      boardModel: this.boardModel,
      taskTracker: this.taskTracker,
    });

    [this.endFlowController.onOutOfSpace, this.endFlowController.onLevelWon].forEach((signal) => {
      signal.connect(() => {
        this.resolved = true;
      });
    });

    this.boardView.pixi.ticker.add(this.update);
    this.boardView.on('destroyed', () => {
      this.boardView.pixi.ticker.remove(this.update);
      this.boardViewRt.destroy(true);
    });
  }

  private stopped = false;

  public stop(): void {
    this.stopped = true;
  }

  private readonly waitTimes = [
    { min: 800, max: 1900 },
    { min: 600, max: 1800 },
    { min: 300, max: 1400 },
    { min: 0, max: 1000 },
    { min: 0, max: 600 },
  ];

  private async waitBetweenMoves(): Promise<void> {
    const waitTime = this.waitTimes[this.difficultyLevel] ?? this.waitTimes[this.waitTimes.length - 1];
    await this.boardView.wait(MathUtils.rnd.between(waitTime.min, waitTime.max) * this.getWaitTimeMultiplier());
  }

  private getWaitTimeMultiplier(): number {
    const remoteConfig = getPlatformRemoteConfig();
    const inputMulti = isMobile.phone || isMobile.tablet ? 1.0 : remoteConfig.feature.duel.desktopTimeMultiplier;
    const untilFirstWin = this.firstTime ? 1.25 : 1;
    return inputMulti * untilFirstWin;
  }

  public async start(): Promise<void> {
    // delay init move
    await this.boardView.wait(MathUtils.rnd.between(700, 1500));

    while (!this.resolved && !this.stopped) {
      await this.makeMove();
      await this.waitBetweenMoves();
    }
  }

  public async makeMove(): Promise<void> {
    if (this.resolved) return;

    if (this.endFlowController.checkOutOfSpace()) return;

    const output = this.strategy.getNextMove({
      boardModel: this.boardModel,
      levelData: this.levelData,
      taskTracker: this.taskTracker,
      trayModel: this.trayModel,
    });

    const fromModel = this.trayModel.getItems()[output.fromStack];

    const targetStack = this.boardModel.getStack(output.toStack.x, output.toStack.y);
    if (!targetStack) {
      return;
    }

    this.boardModel.makeMove(targetStack.id, fromModel.removeAllTiles());

    const shouldRefillTray = !this.trayModel.hasActiveTiles();
    if (shouldRefillTray) {
      this.trayModel.refillItems();
    }

    while (!this.boardModel.isIdle()) {
      await this.boardView.wait(10);
    }

    this.endFlowController.checkLevelWon();
    this.endFlowController.checkOutOfSpace();

    this.boardModel.onScoreUpdated(this.taskTracker.getHexTally());
  }

  public update = (): void => {
    this.boardModel.update(this.boardView.pixi.ticker.deltaTime);

    this.boardView.pixi.renderer.render({
      container: this.boardView,
      target: this.boardViewRt,
      transform: new Matrix(1, 0, 0, 1, 200, 200),
    });
  };
}
